import { useContext, useEffect, useState } from "react";
import { StyleContext } from "../providers/StyleContextProvider";
import { createSearchParams, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { updateApplicationStatusByEmail, viewCandidateApplicationByEmail } from "../services/RecruiterService";
import { LoggedRecruiter } from "../models/LoggedRecruiter";
import { AuthContext } from "../providers/AuthContextProvider";
import useErrorPage from "../hooks/useErrorPage";
import { Button } from "../components/Shared/Buttons/Button";
import { useMediaQuery } from "react-responsive";
import { ReactComponent as Crown } from '../assets/svg/crown.svg';
import { ReactComponent as Calender } from '../assets/svg/calendar.svg';
import { SingleSelect } from "../components/Shared/SingleSelect";
import { SelectInputs } from "../models/SelectInputs";
import { PopUpWindow } from "../components/Shared/PopUpWindow";
import { Tabs } from "../components/Shared/Tabs";
import { TabItem } from "../models/TabItem";
import { ProfileTab } from "../components/ApplicationView/ProfileTab";
import { InfoTab } from "../components/ApplicationView/InfoTab";
import { CVTab } from "../components/ApplicationView/CVTab";
import { CoverLetterTab } from "../components/ApplicationView/CoverLetterTab";
import { NotesTab } from "../components/ApplicationView/NotesTab";
import { AnswersTab } from "../components/ApplicationView/AnswerTab";
import { CommonTexts as Texts } from '../helpers/LayoutTexts';
import { formatDateWithSuperscript } from "../helpers/Common";
import { XpressJobsTitle } from "../components/Shared/XpressJobsTitle";
import { QuizTab } from "../components/ApplicationView/QuizTab";
import { ApplicationViewLoader } from "../components/Loaders/ApplicationViewLoader";
import { useAlert } from "../hooks/useAlert";

export const ApplicationViewPage: React.FC = () => {
    const navigate = useNavigate(); 
    let params = useParams();
    const id = params.refId!;    
    const styleContext = useContext(StyleContext);
    const styles = styleContext.getComponentStyle("applicationViewPage");
    const authContext = useContext(AuthContext);
    const recruiter: LoggedRecruiter | null = authContext.getLoggedRecruiter();
    const [applicantData, setApplicantData] = useState<any>();
    const [profilePictureURL, setProfilePictureURL] = useState<string>("");
    const [hoveredPremiumCandidate, setHoveredPremiumCandidate] = useState<boolean>(false);  
    const [applicationStatusId, setApplicationStatusId] = useState<string>(""); 
    const [loading, setLoading] = useState<boolean>(true);
    const [openMessageDialog, setOpenMessageDialog] = useState<boolean>(false);
    const [tabItems, setTabItems] = useState<TabItem[]>([]);
    const [activeTab, setActiveTab] = useState<string>("");
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const { Alert, openAlert } = useAlert();
    const [toErrorPage] = useErrorPage();

    useEffect(() => {                 
        viewApplication();      
    }, []);

    useEffect(() => {
        if (applicantData) {
            const defaultTab = applicantData.candidate.isPremium ? "Profile" : "CV";
            setActiveTab(defaultTab);
            setApplicationStatusId(applicantData.applicationStatus.applicationStatusId.toString());
            let tabsList = [
                (applicantData.candidate.isPremium) && { label: "Profile", value: "Profile", component: <ProfileTab candidate={applicantData.candidate} styles={styles.profileTab}/> },
                (applicantData.candidate.isPremium) && { label: "Info", value: "Info", component: <InfoTab candidate={applicantData.candidate} preferredSectors={applicantData.preferredSectors} styles={styles.infoTab}/> },
                ...(applicantData.questionsAndAnswers !== null ? [{ label: "Answers", value: "Answers", component: <AnswersTab questionsAndAnswers={applicantData.questionsAndAnswers} candidateResult={applicantData.candidateResult} styles={styles.answersTab}/> }] : []),
                { label: "CV", value: "CV", component: <CVTab candidate={applicantData.candidate} styles={styles.cvTab} cvTranslationText={applicantData.cvTranslationText!} fileName={applicantData.cvFileName!}/> },
                (applicantData.jobQuizzesResults) && { label: "Quiz Answers", value: "QuizAnswers", component: <QuizTab quizResults={applicantData.jobQuizzesResults} styles={styles.quizTab}/> },
                (applicantData.coverLetter) && { label: "Cover Letter", value: "CoverLetter", component: <CoverLetterTab coverLetter={applicantData.coverLetter} styles={styles.coverLetterTab}/> },
                { label: "Notes", value: "Notes", component: <NotesTab jobApplicationId={applicantData.jobApplicationId} preApplicationId={applicantData.preApplicationId} accessKey={applicantData.accessKey} isCVLess={applicantData.job.isPreApplicationFormEnabled} notes={applicantData.notes} styles={styles.notesTab} onNoteAdded={viewApplication}/> }
            ];
            const newTabItems = tabsList.filter(Boolean) as TabItem[];
            setTabItems(newTabItems);
        }
    }, [applicantData]);

    const statusOptions = applicantData?.applicationStages.map((status: any) => ({
        value: status.applicationStatusId,
        option: (
            <div style={styles.moveToWrapper.actions.optionWrapper}>
                <span style={{...styles.moveToWrapper.actions.optionDot, backgroundColor: status.colorCode}}/>
                <div style={styles.moveToWrapper.actions.optionWrapper.status}>{status.status}</div>
            </div>
        )
    })) || [];

    const actionsSelectInputs: SelectInputs = {
        default: applicantData?.applicationStatus.status,
        options: statusOptions
    }

    function lightenColor(hex: string, alpha: number): string {
        // Remove any leading/trailing whitespace from the hex string
        hex = hex.trim();
        // Converts a hex color string to an rgba color string with given transparency
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    const closeMessageDialog = () => {
        setOpenMessageDialog(false);
    }

    const handleViewAllApplications = () => {
        if(recruiter === null) {
            const applicationUrl = `/recruiter/vacancy/applications/${applicantData!.job.jobId}?title=${applicantData!.job.jobTitle}`;
            const params = { callbackUrl: applicationUrl, message: "Please login to view all received applications." };
            navigate({
                pathname: '/recruiter/account/login',
                search: `?${createSearchParams(params)}`
            });
        }
        else {
            const params = { title: applicantData!.job.jobTitle };
            navigate({
                pathname: `/recruiter/vacancy/applications/${applicantData!.job.jobId}`,
                search: `?${createSearchParams(params)}`
            });
        }
    }

    async function viewApplication() {        
        await viewCandidateApplicationByEmail(id)
        .then((response) => {            
            setApplicantData(response);
            if (response.profilePicture) {
                setProfilePictureURL(`data:image/jpeg;base64,${response.profilePicture}`);
            } 
            else {
                setProfilePictureURL(Texts.defaultUser);
            }
            setLoading(false);
        })    
        .catch((error) => {            
            toErrorPage(error);                
        });       
    }

    async function updateStatus () {
        const updateStatusId = parseInt(applicationStatusId);
        const jobId = applicantData!.job.jobId;
        await updateApplicationStatusByEmail(jobId, updateStatusId!, applicantData!.jobApplicationId, applicantData!.preApplicationId, applicantData!.accessKey)
        .then((response) => {
            closeMessageDialog();
            if (response.status){
                setApplicantData((prev: any) => ({
                    ...prev,
                    applicationStatus: response.applicationStatus
                }));
                openAlert("success", response.message);
            }
        })
        .catch((error) => {            
            toErrorPage(error);
        });
    }

    return (
        <>
            <div style={styles.mainWrapper}>
                <div style={styles.leftWrapper}>
                    <div style={styles.header}>
                        <XpressJobsTitle style={styles.header.title}/>
                    </div>
                </div>
                <div style={styles.rightWrapper}>
                    {!isMobile && <div style={styles.header}>
                        <XpressJobsTitle style={styles.header.title}/>
                    </div>}
                    {loading ? <ApplicationViewLoader/> :
                    <>
                        <div style={styles.title}>{applicantData?.job.jobTitle}</div>
                        <Button style={styles.button} onClick={handleViewAllApplications}>{"View All Applications"}</Button>
                        <div style={styles.applicantInfoContainer}>
                            <div style={styles.applicantInfoWrapper}>
                                <img style={styles.profileImage} src={profilePictureURL} alt="profile picture" />
                                <div style={styles.detailsWrapper}>
                                    <div style={styles.detailsWrapper.nameWrapper}>
                                        <div style={styles.detailsWrapper.name}>{applicantData?.candidate.forename} {applicantData?.candidate.surname}</div>
                                        {applicantData?.candidate.isPremium && 
                                            <div 
                                                style={styles.detailsWrapper.crownWrapper}
                                                onMouseEnter={() => setHoveredPremiumCandidate(true)} 
                                                onMouseLeave={() => setHoveredPremiumCandidate(false)}
                                            >
                                                <Crown style={styles.detailsWrapper.crownIcon}/>
                                                {hoveredPremiumCandidate && <div style={styles.premiumMessage}>
                                                    {"Premium Candidate"}
                                                    <div style={styles.premiumMessage.pointer}></div>
                                                </div>}
                                            </div>
                                        }
                                    </div>
                                    <div style={styles.detailsWrapper.email}>{applicantData?.candidate.email}</div>
                                    {applicantData?.appliedDate && <div style={styles.detailsWrapper.appliedDate}>
                                        <Calender style={styles.detailsWrapper.calendarIcon}/>
                                        <div style={styles.detailsWrapper.text}>
                                            <span style={styles.detailsWrapper.applied}>{"Applied"}</span>
                                            {formatDateWithSuperscript(applicantData.appliedDate)}
                                        </div>
                                    </div>}
                                    <div style={styles.detailsWrapper.statusWrapper}>
                                        {applicantData && <div 
                                            style={{
                                                ...styles.detailsWrapper.statusBadge, color: applicantData.applicationStatus.colorCode, backgroundColor: lightenColor(applicantData.applicationStatus.colorCode, 0.15), 
                                                ...(applicantData.applicationStatus.status == "Booked for Interview 1" && styles.detailsWrapper.statusBadge.interviewOne),
                                                ...(applicantData.applicationStatus.status == "Booked for Interview 2" && styles.detailsWrapper.statusBadge.interviewTwo),
                                                ...(applicantData.applicationStatus.status == "Screening Incomplete" && styles.detailsWrapper.statusBadge.screeningIncomplete),
                                                ...(applicantData.applicationStatus.status == "Interview No Show" && styles.detailsWrapper.statusBadge.noShow)
                                            }}
                                        >
                                            {applicantData.applicationStatus.status}
                                        </div>}
                                    </div>
                                </div>
                            </div>
                            {applicantData && (
                                <div style={styles.moveSection}>
                                    <div style={styles.moveToWrapper}>
                                        <div style={styles.moveToWrapper.title}>{"Move to"}</div>
                                        <SingleSelect 
                                            styles={styles.moveToWrapper.actions}  
                                            selectInputs={actionsSelectInputs} 
                                            getSelected={(selectedValue) => {
                                                setApplicationStatusId(selectedValue);
                                            }}
                                        />
                                    </div>
                                    <div style={styles.moveButton}>
                                        <Button style={styles.moveButton.button} type="button" onClick={() => setOpenMessageDialog(true)}>{"MOVE"}</Button>
                                    </div>
                                </div>
                            )}
                            {tabItems.length > 0 && (
                                <Tabs 
                                    key={applicantData?.notes}
                                    styles={styles.tabs} 
                                    tabItems={tabItems} 
                                    selectedTab={activeTab}
                                    getSelectedTab={setActiveTab}
                                />
                            )}
                        </div>
                    </>}
                </div>
            </div>
            <PopUpWindow styles={styles.dialog} open={openMessageDialog} toggle={closeMessageDialog}>
                <p style={styles.dialog.title}>{"Please Confirm"}</p>
                <div style={styles.dialog.message}>{"This will move the selected item. Do you want to continue?"}</div>
                <div style={styles.dialog.buttons}>
                    <Button style={styles.dialog.move} type="button" onClick={updateStatus}>{"Move"}</Button>
                    <Button style={styles.dialog.cancel} type="button" onClick={closeMessageDialog}>{"Cancel"}</Button>
                </div> 
            </PopUpWindow>
            <Alert />
        </>
    )
}